import React, { useEffect, useMemo, useState } from "react";
import {
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  CheckIcon,
} from "@heroicons/react/24/outline";
import api from "../services/api";
import { getRightLabel } from "../account/rightCatalog";

const getRightRows = (grades) => {
  const names = [...new Set(grades.flatMap((grade) => grade.Droits.map((right) => right.Nom)))];
  return names
    .map((name) => {
      const rights = Object.fromEntries(
        grades.map((grade) => [grade.GradeID, grade.Droits.find((right) => right.Nom === name) || null])
      );
      const sample = Object.values(rights).find(Boolean);
      return { name, type: sample?.Type || "PERMISSION", rights };
    })
    .sort((left, right) => {
      if (left.type !== right.type) return left.type === "LIMIT" ? -1 : 1;
      return getRightLabel(left.name).localeCompare(getRightLabel(right.name), "fr");
    });
};

const RightsManagementAdmin = () => {
  const [grades, setGrades] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const rows = useMemo(() => getRightRows(grades), [grades]);
  const changedRights = useMemo(() => (
    grades.flatMap((grade) => grade.Droits).filter(
      (right) => Number(values[right.DroitID]) !== Number(right.Valeur)
    )
  ), [grades, values]);

  const loadRights = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/admin/rights");
      const nextGrades = response.data.grades || [];
      setGrades(nextGrades);
      setValues(Object.fromEntries(
        nextGrades.flatMap((grade) => grade.Droits).map((right) => [right.DroitID, right.Valeur])
      ));
    } catch (err) {
      setError(err.response?.data?.error || "Impossible de charger le moteur des droits.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRights();
  }, []);

  const handleSave = async () => {
    if (changedRights.length === 0) return;
    if (changedRights.some((right) => values[right.DroitID] === "")) {
      setError("Chaque limite modifiée doit contenir une valeur.");
      return;
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const response = await api.put("/admin/rights", {
        updates: changedRights.map((right) => ({
          DroitID: right.DroitID,
          Valeur: Number(values[right.DroitID]),
        })),
      });
      setSuccess(`${response.data.updated} droit${response.data.updated > 1 ? "s" : ""} mis à jour.`);
      await loadRights();
    } catch (err) {
      setError(err.response?.data?.error || "Impossible d'enregistrer les droits.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="overflow-hidden rounded-lg border border-sky-500/30 bg-[linear-gradient(135deg,rgba(2,6,23,0.98),rgba(15,23,42,0.9))] shadow-[0_24px_80px_rgba(2,6,23,0.38)]">
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-white/10 px-5 py-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-sky-500/15 text-sky-300">
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase text-sky-300">Paramètres des grades</p>
            <h2 className="mt-1 text-xl font-semibold text-slate-50">Moteur des droits</h2>
          </div>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || changedRights.length === 0}
          className="inline-flex items-center gap-2 rounded-md bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {saving ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <CheckIcon className="h-4 w-4" />}
          {saving ? "Enregistrement..." : `Enregistrer${changedRights.length ? ` (${changedRights.length})` : ""}`}
        </button>
      </div>

      <div className="p-5 sm:p-6">
        <p className="mb-4 text-sm leading-6 text-slate-400">
          Les permissions acceptent 0 (désactivé) ou 1 (activé). La valeur -1 rend une limite illimitée. Les tarifs sont saisis en centimes et la portée du journal vaut 0 (aucun), 1 (personnel) ou 2 (organisation).
          Les modifications s'appliquent aux prochaines requêtes des comptes concernés.
        </p>
        {error && <p className="mb-4 rounded-md border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">{error}</p>}
        {success && <p className="mb-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-200">{success}</p>}
        {loading ? (
          <p className="text-sm text-amber-200">Chargement des droits...</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-white/10">
            <table className="min-w-[1100px] w-full divide-y divide-white/10 text-sm">
              <thead className="bg-slate-900/90">
                <tr>
                  <th className="sticky left-0 z-10 min-w-64 bg-slate-900 px-4 py-3 text-left font-semibold text-slate-200">Droit</th>
                  {grades.map((grade) => (
                    <th key={grade.GradeID} className="min-w-36 px-3 py-3 text-center font-semibold text-slate-200">
                      {grade.Nom}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 bg-slate-950/50">
                {rows.map((row) => (
                  <tr key={row.name} className="hover:bg-white/[0.03]">
                    <th className="sticky left-0 z-10 bg-slate-950 px-4 py-3 text-left">
                      <span className="block font-medium text-slate-100">{getRightLabel(row.name)}</span>
                      <span className="mt-1 block font-mono text-[11px] font-normal text-slate-500">{row.name}</span>
                    </th>
                    {grades.map((grade) => {
                      const right = row.rights[grade.GradeID];
                      if (!right) return <td key={grade.GradeID} className="px-3 py-3 text-center text-slate-600">—</td>;
                      return (
                        <td key={grade.GradeID} className="px-3 py-3 text-center">
                          {right.Type === "PERMISSION" ? (
                            <select
                              aria-label={`${row.name} - ${grade.Nom}`}
                              value={values[right.DroitID] ?? right.Valeur}
                              onChange={(event) => setValues((current) => ({
                                ...current,
                                [right.DroitID]: Number(event.target.value),
                              }))}
                              className="w-full rounded-md border border-white/10 bg-slate-900 px-2 py-2 text-slate-100 outline-none focus:border-sky-400"
                            >
                              <option value={1}>Activé</option>
                              <option value={0}>Désactivé</option>
                            </select>
                          ) : right.Type === "ENUM" ? (
                            <select
                              aria-label={`${row.name} - ${grade.Nom}`}
                              value={values[right.DroitID] ?? right.Valeur}
                              onChange={(event) => setValues((current) => ({ ...current, [right.DroitID]: Number(event.target.value) }))}
                              className="w-full rounded-md border border-white/10 bg-slate-900 px-2 py-2 text-slate-100 outline-none focus:border-sky-400"
                            >
                              <option value={0}>Aucun</option>
                              <option value={1}>Personnel</option>
                              <option value={2}>Organisation</option>
                            </select>
                          ) : (
                            <input
                              type="number"
                              aria-label={`${row.name} - ${grade.Nom}`}
                              min={right.ValeurMin}
                              max={right.ValeurMax}
                              value={values[right.DroitID] ?? right.Valeur}
                              onChange={(event) => setValues((current) => ({
                                ...current,
                                [right.DroitID]: event.target.value,
                              }))}
                              className="w-full rounded-md border border-white/10 bg-slate-900 px-2 py-2 text-center text-slate-100 outline-none focus:border-sky-400"
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
};

export default RightsManagementAdmin;
